import * as React from 'react';
import { DocumentMimeKind } from '../types';

export function ViewerToolbar(props: {
    mimeKind: DocumentMimeKind;
    pageIndex: number;
    pageCount: number;
    setPageIndex: (n: number | ((p: number) => number)) => void;
    scale: number;
    fitScale: number;
    viewAway: boolean;
    zoomIn: () => void;
    zoomOut: () => void;
    zoomReset: () => void;
    allowRotate?: boolean;
    rotateTurns: number;
    orientationDirty: boolean;
    rotateSaving: boolean;
    rotateBy: (delta: number) => void;
    saveOrientation: () => void;
    onOrientedSave?: (blob: Blob, fileName: string) => void | Promise<void>;
    hitsLength: number;
    searchStatus: string;
    goPrevHit: () => void;
    goNextHit: () => void;
    hosted?: boolean;
}) {
    return (
        <div className={props.hosted ? 'dvToolbar dvToolbarHosted' : 'dvToolbar'}>
            <div className="dvToolbarRow">
                {props.mimeKind === 'pdf' && (
                    <div className="dvSeg" role="group" aria-label="Page">
                        <button type="button" className="dvBtn" aria-label="Previous page" disabled={props.pageIndex <= 0} onClick={() => props.setPageIndex(p => Math.max(0, p - 1))}>‹</button>
                        <span className="dvMeta" aria-live="polite">Page {props.pageIndex + 1} of {props.pageCount}</span>
                        <button type="button" className="dvBtn" aria-label="Next page" disabled={props.pageIndex >= props.pageCount - 1} onClick={() => props.setPageIndex(p => Math.min(props.pageCount - 1, p + 1))}>›</button>
                    </div>
                )}
                <div className="dvSeg" role="group" aria-label="Zoom">
                    <button type="button" className="dvBtn" aria-label="Zoom out" onClick={props.zoomOut}>−</button>
                    <span className="dvMeta">{Math.round(props.scale * props.fitScale * 100)}%</span>
                    <button type="button" className="dvBtn" aria-label="Zoom in" onClick={props.zoomIn}>+</button>
                </div>
                <button
                    type="button"
                    className={`dvFitBtn${props.viewAway ? ' dvFitBtnLoud' : ''}`}
                    aria-label="Fit to view"
                    title="Fit document to view"
                    onClick={props.zoomReset}
                >
                    Fit
                </button>
                {props.allowRotate && (
                    <div className="dvSeg" role="group" aria-label="Rotate">
                        <button type="button" className="dvBtn" aria-label="Rotate left" title="Rotate left" onClick={() => props.rotateBy(-1)}>↺</button>
                        <button type="button" className="dvBtn" aria-label="Rotate right" title="Rotate right" onClick={() => props.rotateBy(1)}>↻</button>
                        {!!props.onOrientedSave && (
                            <button
                                type="button"
                                className="dvBtn"
                                disabled={(!props.rotateTurns && !props.orientationDirty) || props.rotateSaving || props.mimeKind !== 'image'}
                                aria-label="Save orientation"
                                title="Save orientation to file"
                                onClick={() => { props.saveOrientation(); }}
                            >
                                {props.rotateSaving ? '…' : 'Save'}
                            </button>
                        )}
                    </div>
                )}
                {props.allowRotate && (!!props.rotateTurns || props.orientationDirty) && (
                    <span className="dvMeta">
                        {props.rotateTurns ? `${props.rotateTurns * 90}°` : 'Aligned'}
                        {props.orientationDirty && props.onOrientedSave ? ' · unsaved' : ''}
                        {props.orientationDirty && !props.onOrientedSave ? ' · view only' : ''}
                    </span>
                )}

                {props.hitsLength > 0 && (
                    <>
                        <div className="dvDivider" />
                        <div className="dvSeg" role="group" aria-label="Matches">
                            <button type="button" className="dvBtn" aria-label="Previous match" onClick={props.goPrevHit}>‹</button>
                            <span className="dvMeta dvStatusHit" aria-live="polite">{props.searchStatus}</span>
                            <button type="button" className="dvBtn" aria-label="Next match" onClick={props.goNextHit}>›</button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
